import type { BoardContent } from './doc';
import {
  type BoardElement,
  type ConnectorElement,
  createElement,
  createFrame,
  type FontFamily,
  type Frame,
  type FrameBackground,
  type ShapeElement,
  type ShapeKind,
  type StickyElement,
  type TextAlign,
} from './elements';
import { ordersBetween } from './order';

export const TEMPLATES = [
  { id: 'blank', name: 'Blank', description: 'An empty dotted frame' },
  { id: 'brainstorm', name: 'Brainstorm', description: 'A central topic with ideas around it' },
  {
    id: 'retro',
    name: 'Retrospective',
    description: 'What went well, what to improve, action items',
  },
  { id: 'kanban', name: 'Kanban', description: 'To do, in progress and done columns' },
  {
    id: 'swot',
    name: 'SWOT analysis',
    description: 'Strengths, weaknesses, opportunities and threats',
  },
  { id: 'weekly', name: 'Weekly planner', description: 'A column for every day of the week' },
] as const;

export type TemplateId = (typeof TEMPLATES)[number]['id'];

export function isTemplateId(value: unknown): value is TemplateId {
  return TEMPLATES.some((t) => t.id === value);
}

interface TextOptions {
  fontSize?: number;
  bold?: boolean;
  color?: string;
  align?: TextAlign;
  font?: FontFamily;
  locked?: boolean;
}

/** Collects the elements of one template frame; z-order follows insertion order. */
class FrameBuilder {
  readonly frame: Frame;
  private readonly elements: BoardElement[] = [];

  constructor(
    background: FrameBackground,
    private readonly author: string,
  ) {
    this.frame = createFrame({ background });
  }

  text(x: number, y: number, w: number, text: string, opts: TextOptions = {}) {
    const fontSize = opts.fontSize ?? 24;
    const lines = text.split('\n').length;
    return this.push(
      createElement('text', {
        frameId: this.frame.id,
        x,
        y,
        w,
        h: Math.ceil(lines * fontSize * 1.3),
        text,
        fontSize,
        bold: opts.bold ?? false,
        color: opts.color ?? '#202124',
        align: opts.align ?? 'left',
        font: opts.font ?? 'sans',
        locked: opts.locked ?? false,
      }),
    );
  }

  sticky(x: number, y: number, text: string, color: string, size = 200): StickyElement {
    return this.push(
      createElement('sticky', {
        frameId: this.frame.id,
        x,
        y,
        w: size,
        h: size,
        text,
        color,
        author: this.author,
      }),
    );
  }

  shape(
    kind: ShapeKind,
    x: number,
    y: number,
    w: number,
    h: number,
    style: Partial<ShapeElement> = {},
  ): ShapeElement {
    return this.push(
      createElement('shape', { frameId: this.frame.id, kind, x, y, w, h, ...style }),
    );
  }

  connect(from: BoardElement, to: BoardElement, style: Partial<ConnectorElement> = {}) {
    return this.push(
      createElement('connector', {
        frameId: this.frame.id,
        start: { x: from.x, y: from.y, bindId: from.id, anchor: null },
        end: { x: to.x, y: to.y, bindId: to.id, anchor: null },
        ...style,
      }),
    );
  }

  /** A locked colored header bar above a locked column, the building block of column templates. */
  column(x: number, y: number, w: number, h: number, title: string, header: string, body: string) {
    this.shape('roundRect', x, y, w, 76, {
      fill: header,
      stroke: 'transparent',
      strokeWidth: 0,
      text: title,
      textColor: '#ffffff',
      fontSize: 26,
      locked: true,
    });
    this.shape('roundRect', x, y + 92, w, h - 92, {
      fill: body,
      stroke: 'transparent',
      strokeWidth: 0,
      locked: true,
    });
  }

  private push<T extends BoardElement>(el: T): T {
    this.elements.push(el);
    return el;
  }

  build(): BoardContent {
    // Locked scaffolding (titles, columns, quadrants) always sits below what
    // people add, so a note dragged across columns never slides underneath one.
    const ordered = [
      ...this.elements.filter((el) => el.locked),
      ...this.elements.filter((el) => !el.locked),
    ];
    const keys = ordersBetween(null, null, ordered.length);
    ordered.forEach((el, i) => {
      el.z = keys[i] as string;
    });
    return { frames: [this.frame], elements: ordered };
  }
}

const dots: FrameBackground = { pattern: 'dots', color: '#ffffff' };

function brainstorm(b: FrameBuilder): void {
  b.text(-500, -680, 1000, 'Brainstorm', {
    fontSize: 48,
    bold: true,
    align: 'center',
    locked: true,
  });
  const center = b.sticky(-130, -130, 'Main topic', '#f8bbd0', 260);
  const colors = ['#fff176', '#ffcc80', '#d1c4e9', '#b3e5fc', '#c5e1a5', '#b2dfdb'];
  colors.forEach((color, i) => {
    const angle = ((-90 + i * 60) * Math.PI) / 180;
    const idea = b.sticky(Math.cos(angle) * 430 - 100, Math.sin(angle) * 380 - 100, 'Idea', color);
    b.connect(center, idea, { route: 'curved', endCap: 'none', color: '#5f6368' });
  });
}

function retro(b: FrameBuilder): void {
  b.text(-780, -500, 1560, 'Retrospective', {
    fontSize: 48,
    bold: true,
    align: 'center',
    locked: true,
  });
  const columns = [
    ['😀  Went well', '#34a853', '#e6f4ea'],
    ['🤔  To improve', '#f29900', '#fef7e0'],
    ['🚀  Action items', '#4285f4', '#e8f0fe'],
  ] as const;
  columns.forEach(([title, header, body], i) => {
    const x = -780 + i * 530;
    b.column(x, -400, 500, 820, title, header, body);
    b.sticky(x + 30, -280, 'Add your notes here', '#fff176');
  });
}

function kanban(b: FrameBuilder): void {
  b.text(-780, -500, 1560, 'Kanban board', {
    fontSize: 48,
    bold: true,
    align: 'center',
    locked: true,
  });
  const columns = [
    ['📋  To do', '#5f6368'],
    ['⚙️  In progress', '#1a73e8'],
    ['✅  Done', '#188038'],
  ] as const;
  columns.forEach(([title, header], i) => {
    b.column(-780 + i * 530, -400, 500, 820, title, header, '#f1f3f4');
  });
  ['Task 1', 'Task 2', 'Task 3'].forEach((task, i) => {
    b.sticky(-750 + (i % 2) * 230, -280 + Math.floor(i / 2) * 230, task, '#fff176');
  });
}

function swot(b: FrameBuilder): void {
  b.text(-650, -540, 1300, 'SWOT analysis', {
    fontSize: 48,
    bold: true,
    align: 'center',
    locked: true,
  });
  const quadrants = [
    ['Strengths', '#e6f4ea', '#188038', -650, -430],
    ['Weaknesses', '#fce8e6', '#c5221f', 10, -430],
    ['Opportunities', '#e8f0fe', '#1967d2', -650, 10],
    ['Threats', '#fef7e0', '#e37400', 10, 10],
  ] as const;
  for (const [title, fill, color, x, y] of quadrants) {
    b.shape('roundRect', x, y, 640, 420, {
      fill,
      stroke: 'transparent',
      strokeWidth: 0,
      locked: true,
    });
    b.text(x + 28, y + 24, 400, title, { fontSize: 32, bold: true, color, locked: true });
  }
}

function weekly(b: FrameBuilder): void {
  b.text(-923, -500, 1846, 'Weekly planner', {
    fontSize: 48,
    bold: true,
    align: 'center',
    locked: true,
  });
  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  days.forEach((day, i) => {
    const weekend = i >= 5;
    b.column(-923 + i * 266, -400, 250, 760, day, weekend ? '#9334e6' : '#1a73e8', '#f8f9fa');
  });
}

/**
 * Builds the frames and elements of a template, with fresh ids.
 * `author` is shown on sticky notes the template creates.
 */
export function buildTemplate(id: TemplateId, author = ''): BoardContent {
  const background = id === 'weekly' ? { pattern: 'grid' as const, color: '#ffffff' } : dots;
  const builder = new FrameBuilder(background, author);
  switch (id) {
    case 'brainstorm':
      brainstorm(builder);
      break;
    case 'retro':
      retro(builder);
      break;
    case 'kanban':
      kanban(builder);
      break;
    case 'swot':
      swot(builder);
      break;
    case 'weekly':
      weekly(builder);
      break;
    default:
      break;
  }
  return builder.build();
}
